import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import AdminDataTable from "../../components/common/AdminDataTable";
import { faqColumns } from "../../tables/faqColumns";
import { can } from "../../utils/can";

export default function Faq() {
  const PAGE_SIZE = 10;
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  const [faqs, setFaqs] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    id: null,
    question: "",
    answer: "",
    status: true,
  });

  const [formErrors, setFormErrors] = useState({
    question: "",
    answer: "",
  });

  // Fetch FAQ list
  const fetchFaqs = useCallback(async () => {
    try {
      setListLoading(true);
      const query = new URLSearchParams({
        page,
        pageSize,
        sortBy,
        sortOrder,
      });

      const res = await fetch(`${API_URL}/api/admin/faq-list?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (result.success) {
        setFaqs(result.data || []);
        setTotalPages(Math.ceil(result.totalCount / pageSize));
      } else {
        setFaqs([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Fetch FAQs Error:", err);
      setFaqs([]);
      setTotalPages(1);
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!form.question.trim()) errors.question = "Question is required.";
    if (!form.answer.trim()) errors.answer = "Answer is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit form (Create or Update)
  const handleSubmit = async (e) => {
    if(can("faq","create") === false){
        alert("You don't have permission.");
        return;
      }

    e.preventDefault();
    if (!validateForm()) return;

    const method = "POST";
    const url = form.id
      ? `${API_URL}/api/admin/faq-list/update/${form.id}`
      : `${API_URL}/api/admin/faq-create`;

    try {
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: form.question,
          answer: form.answer,
          status: form.status ? "Active" : "Deactive",
        }),
      });

      const result = await res.json();
      if (result.success) {
        
        setMessage(`FAQ ${form.id ? "updated" : "added"} successfully!`)
        setForm({ id: null, question: "", answer: "", status: true });
        setFormErrors({ question: "", answer: "" });
        fetchFaqs();
      } else {
        alert(result.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Submit FAQ Error:", err);
      alert("Server error.");
    }
  };

  // Edit FAQ
  const handleEdit = (faq) => {
    if(can("faq","edit") === false){
        alert("You don't have permission.");
        return;
      }

    setForm({
      id: faq._id,
      question: faq.question,
      answer: faq.answer,
      status: faq.status === "Active",
    });
    setFormErrors({ question: "", answer: "" });
  };

  // Delete FAQ
  const handleDelete = async (id) => {
    if(can("faq","delete") === false){
        alert("You don't have permission.");
        return;
      }

    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/faq-list/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        // alert("FAQ deleted successfully!");
        fetchFaqs();
      } else {
        alert(result.message || "Failed to delete FAQ.");
      }
    } catch (err) {
      console.error("Delete FAQ Error:", err);
      alert("Server error.");
    }
  };

  const columns = faqColumns(handleEdit, handleDelete, PAGE_SIZE);

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="FAQ" />

        <div className="right_col" role="main">
          

          {/* FAQ Form */}
          <div className="x_panel">
            <div className="x_title">
              <h2>FAQ Entry</h2>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <form onSubmit={handleSubmit}>
                 <div className="filtercont">
                <div className="row">
                  <div className="col-12 col-sm-8 pr-lg-0 mb-3">
                    <label>Question</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.question}
                      onChange={(e) => setForm({ ...form, question: e.target.value })}
                    />
                    {formErrors.question && (
                      <p className="text-danger">{formErrors.question}</p>
                    )}
                  </div>
                  <div className="col-12" />
                  <div className="col-12 col-sm-8 pr-lg-0 mb-3">
                    <label>Answer</label>
                    <textarea
                      className="form-control"
                      value={form.answer}
                      onChange={(e) => setForm({ ...form, answer: e.target.value })}
                    />
                    {formErrors.answer && (
                      <p className="text-danger">{formErrors.answer}</p>
                    )}
                  </div>
                  <div className="col-12" />
                  <div className="col-12 col-sm-8 pr-lg-0">
                    <input
                      type="checkbox"
                      id="active"
                      checked={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.checked })}
                    />
                    <label htmlFor="active" className="form-label">
                      Active/Deactive
                    </label>
                  </div>
                  <div className="col-12" />
                  <div className="col-12 col-sm-8 pr-lg-0 text-right">
                    <button type="submit" className="btn sitebtn">
                      {form.id ? "Update" : "Submit"}
                    </button>
                    <p className="text-success">{message}</p>
                  </div>
                </div>
                  </div>
              </form>
            </div>
          </div>

          {/* FAQ List */}
          <div className="x_panel">
            <div className="x_title">
              <h2>FAQ List</h2>
              <div className="clearfix" />
            </div>

            <AdminDataTable
              columns={columns}
              data={faqs}
              loading={listLoading}
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
